import { useEffect, useMemo, useState } from "react";
import { IAppImage, roleImages } from "../../../constants/raceImages";
import { useOutletContext } from "react-router-dom";
import {
  BattleModel,
  BattleResultModel,
  BattleStage,
  BetCurrency,
  ErrorResponse,
  IContext,
  IInternalChatMessage,
  IUserSafeModel,
  MatchFinishedEvent,
  MatchFinishedPayload,
  MatchModel,
  MatchStartedEvent,
  MatchStartedPayload,
  RPSMoveOption,
  TurnFinishedEvent,
  TurnFinishedPayload,
} from "../../../types/types";
import { mapRace } from "../../../utils/transformation";
import InternalChat from "../InternalChat/InternalChat";
import { calcHp, generateMessage } from "../../../utils/battle";
import RPSCharacter from "../RPS/RPSCharacter";
import RPSWarningModal from "../RPS/RPSWarningModal";
import { useTranslation } from "react-i18next";
import FightStartScreen from "./FightStartScreen";
import { formatTimeMMSS } from "../../../utils/time";
import RPSSelectOption from "../RPS/RPSSelectOption";
import {
  acceptMatchApi,
  createMatchApi,
  finishBattleApi,
  finishMatchApi,
  getActiveBattleApi,
  getPendingMatchesApi,
  makeMatchMoveApi,
  makeMoveApi,
  startBattleApi
} from "../../../api/apiFunctions";
import { appConfig } from "../../../app/appConfig";
import { getFromStorage } from "../../../utils/imageUtil";

const Fight = ({
                 isFightModalOpen,
                 setIsFightModalOpen,
                 warning,
                 setWarning,
                 showWarning,
                 setShowWarning,
                 matchEventsToHandle
               }: {
    isFightModalOpen: boolean;
    warning: string;
    setWarning: (val: string) => void;
    setIsFightModalOpen: (val: boolean) => void;
    showWarning: boolean;
    setShowWarning: (val: boolean) => void;
    matchEventsToHandle: (MatchFinishedEvent | TurnFinishedEvent | MatchStartedEvent)[];
  }) => {
    const MAX_ROUNDS = 10;
    const [battleStage, setBattleStage] = useState<BattleStage>(BattleStage.NOT_STARTED);
    const [gameOption, setGameOption] = useState<RPSMoveOption | undefined>();
    const {t} = useTranslation();
    const [messages, setMessages] = useState<IInternalChatMessage[]>([
      {text: t('app.fight.battleStarted'), incoming: true},
    ]);
    const {user, setUser} = useOutletContext<IContext>();
    const [battleTimeLeft, setBattleTimeLeft] = useState<number>(-1);
    const [bet, setBet] = useState<number>(0);
    const hp = 100;
    const [enemyHpLeft, setEnemyHpLeft] = useState(hp);
    const [heroHpLeft, setHeroHpLeft] = useState(hp);
    const [mainCharacterIcon, setMainCharacterIcon] = useState<IAppImage | null>(null);
    const [battleResultMessage, setBattleResultMessage] = useState<string>("");
    const [isFinishModalOpen, setIsFinishModalOpen] = useState(false);
    const [battleModel, setBattleModel] = useState<BattleModel | null>(null);
    const [matchModel, setMatchModel] = useState<MatchModel | null>(null);
    const [battleResult, setBattleResult] = useState<BattleResultModel | null>();
    const [fightCooldownRemaining, setFightCooldownRemaining] = useState<number>(0);
    const [loading, setLoading] = useState(false);
    const [isPvp, setIsPvp] = useState<boolean>(true);
    const [pendingMatches, setPendingMatches] = useState<MatchModel[] | null>(null);
    const [leftPlayer, setLeftPlayer] = useState<IUserSafeModel | null>(null);
    const [rightPlayer, setRightPlayer] = useState<IUserSafeModel | null>(null);

    const fightCooldown = useMemo<number>(() => {
      return Number(appConfig.fightCooldown);
    }, [appConfig.fightCooldown]);

    const calculateCooldown = () => {

      const nowSeconds = new Date().getTime() / 1000;
      const lastFightFinishedAtSeconds = new Date(user.lastBattleStartedAt).getTime() / 1000;
      const difference = nowSeconds - lastFightFinishedAtSeconds;

      if (difference >= Number(fightCooldown)) {
        setFightCooldownRemaining(0);
      } else {
        setFightCooldownRemaining(Math.round(fightCooldown - difference));
      }
    }

    const calculateBattleDuration = () => {

      const nowSeconds = new Date().getTime() / 1000;
      const battleDeadline = new Date(battleModel?.started ?? matchModel?.started ?? 0).getTime() / 1000 + 179;
      const difference = battleDeadline - nowSeconds;

      if (battleTimeLeft === 0) {
        onTimeExpired();
      } else if (difference >= 0) {
        setBattleTimeLeft(Math.round(difference));
      }
    }

    const resetBattleModel = () => {
      setBattleModel(null);
    };

    const closeFight = async () => {
      if (!battleModel && !matchModel) return;

      try {
        if (battleStage === BattleStage.STARTED) {
          if (battleModel) {
            const battleResult = await finishBattle();
            handleFinishedBattle(battleResult);
          } else if (matchModel) {
            await finishMatch();
          }
        } else {
          setWarning("");
          setIsFightModalOpen(false);
        }
      } catch (error: any) {
        const errCode = error.code;
        if (errCode === 4) {
          setWarning("");
          setIsFightModalOpen(false);
        } else {
          setWarning(t(`app.errorCodes.${ error.code }`).toString());
          setShowWarning(true);
        }
      }
    }

    const closeResults = () => {
      setBattleStage(BattleStage.NOT_STARTED);
      setIsFightModalOpen(false);
    }

    const onTimeExpired = () => {
      if (!battleModel && !matchModel) return;
      setBattleTimeLeft(-1);
      closeFight().finally();
    };

    const getActiveBattle = async (): Promise<BattleModel | null> => {
      try {
        const activeBattleResponse = await getActiveBattleApi();
        return activeBattleResponse.battle;
      } catch (error: any) {
        throw error.response.data.error;
      }
    }

    const startBattle = async () => {
      try {
        const startBattleResponse = await startBattleApi({bet: bet.toString()});
        return startBattleResponse.battle;
      } catch (error: any) {
        throw error.response.data.error;
      }
    }

    const startMatch = async () => {
      try {
        const startMatchResponse = await createMatchApi({currency: BetCurrency.TON, bet: bet});
        return startMatchResponse.match;
      } catch (error: any) {
        throw error.response.data.error;
      }
    }

    const makeMove = async () => {
      try {
        if (!gameOption) throw new Error(/*TODO*/);
        if (battleModel) {
          const makeMoveResponse = await makeMoveApi({battleId: battleModel.id, option: gameOption});
          return makeMoveResponse.move;
        } else if (matchModel) {
          await makeMatchMoveApi({matchId: matchModel.id, option: gameOption});
        }

      } catch (error: any) {
        throw error.response.data.error;
      }
    }

    const finishBattle = async () => {
      try {
        if (!battleModel) throw new Error(/*TODO*/);
        const finishBattleResponse = await finishBattleApi({battleId: battleModel.id});
        return finishBattleResponse.result;
      } catch (error: any) {
        throw error.response.data.error;
      }
    }

    const finishMatch = async () => {
      try {
        if (!matchModel) throw new Error(/*TODO*/);
        await finishMatchApi({matchId: matchModel.id});
      } catch (error: any) {
      }
    }

    const finishActiveBattleIfNotNull = async (activeBattle: BattleModel | null) => {
      try {
        if (activeBattle) {
          setBattleModel(activeBattle);
          const finishBattleResponse = await finishBattleApi({battleId: activeBattle.id});
          return finishBattleResponse.result;
        }
        return null;
      } catch (error: any) {
        const errorData = error.response.data.error;
        if (errorData.code !== 4) {
          throw errorData;
        }
      }
    }

    const handleFinishedBattle = (battleResultModel: BattleResultModel | null) => {
      const reward = Number(battleResultModel?.reward);
      switch (battleResultModel?.result) {
        case -1:
          setHeroHpLeft(0);
          break;
        case 1:
          setEnemyHpLeft(0);
          if (reward && reward > 0) {
            setUser({...user, ...{coins: user.coins + reward}})
          }
          break;
        case 0:
          if (reward && reward > 0) {
            setUser({...user, ...{coins: user.coins + reward}})
          }
          break;
      }
      setBattleResult(battleResultModel);
      setBattleStage(BattleStage.FINISHED);
      setBattleResultMessage(generateBattleResultMessage(battleResultModel?.result) ?? "");
    }

    const handleFinishedMatch = (matchResultModel: MatchFinishedPayload) => {
      let bet = Number(matchModel?.bet);
      switch (matchResultModel?.winnerId) {
        case leftPlayer?.id:
          setHeroHpLeft(0);
          if (bet && bet > 0) {
            setUser({...user, ...{tonBalance: user.tonBalance + (bet * 2)}})
          }
          break;
        case rightPlayer?.id:
          setEnemyHpLeft(0);
          bet = 0;
          break;
        default:
          if (bet && bet > 0) {
            setUser({...user, ...{tonBalance: user.tonBalance + bet}})
          }
          break;
      }
      setBattleResult({reward: bet.toString(), result: 0});
      setBattleStage(BattleStage.FINISHED);
      setBattleResultMessage(generateBattleResultMessage(matchResultModel?.winnerId === leftPlayer?.id ? 1 : -1) ?? "");
    }

    const onStartClick = () => {

      if (isPvp) {
        startMatch().then(response => {
          setUser({
            ...user, ...{
              tonBalance: Math.round(user.tonBalance - Number(response.bet))
            }
          });
          setMatchModel(response);
          setLoading(true);
        }).catch((error: ErrorResponse) => {
          setWarning(t(`app.errorCodes.${ error.code }`).toString());
          setShowWarning(true);
        });
      } else {
        startBattle().then(response => {
          setUser({
            ...user, ...{
              lastBattleStartedAt: new Date().toString(), coins: Math.round(user.coins - Number(response.bet))
            }
          });
          setBattleModel(response);
          setBattleStage(BattleStage.STARTED);
        }).catch((error: ErrorResponse) => {
          setWarning(t(`app.errorCodes.${ error.code }`).toString());
          setShowWarning(true);
        });
      }
    };

    const handleGameOptionClick = (option: RPSMoveOption) => {
      setGameOption(option);
    };

    const addMessage = (text: string) => {
      setMessages((prev) => [
        ...prev,
        {
          text: text,
          incoming: true,
        },
      ]);
    }

    const handleGameOptionSubmit = () => {
      if (!battleModel && !matchModel) return;

      if (gameOption) {
        makeMove().then(moveResult => {
          if (!moveResult) return;

          if (battleModel) {
            switch (moveResult.result.toString()) {
              case '1': {
                const response = generateMessage(responses.hit);
                addMessage(response);
                setEnemyHpLeft((curr) => calcHp(curr));
                break;
              }
              case '-1': {
                const response = generateMessage(responses.miss);
                addMessage(response);
                setHeroHpLeft((curr) => calcHp(curr));
                break;
              }
              default: {
                setHeroHpLeft((curr) => calcHp(curr));
                setEnemyHpLeft((curr) => calcHp(curr));
                addMessage(t('app.fight.draw'));
                break;
              }
            }
            setBattleModel({...battleModel, ...{turn: moveResult.turn, score: battleModel.score + moveResult.result}});
          }

        }).catch(error => {
          setWarning(t(`app.errorCodes.${ error.code }`));
          setShowWarning(true);
        });
      }
    };

    const responses = useMemo(() => {
      if (battleModel) {
        const enemyId = battleModel.enemy + 1;

        return {
          miss: t(`app.enemies.lvl${ user.level }.${ enemyId }.player_miss`, {returnObjects: true}) as string [],
          hit: t(`app.enemies.lvl${ user.level }.${ enemyId }.player_hits_enemy`, {returnObjects: true}) as string [],
          win: t(`app.enemies.lvl${ user.level }.${ enemyId }.enemy_dies`, {returnObjects: true}) as string [],
          loose: t(`app.enemies.lvl${ user.level }.${ enemyId }.player_dies`, {returnObjects: true}) as string [],
        }
      }
      return {miss: [''], hit: [''], win: [''], loose: ['']};
    }, [battleModel]);

    const checkGameIsOver = () => {
      return battleModel && battleModel.turn >= battleModel.maxTurns;
    }

    const generateBattleResultMessage = (result?: number) => {
      switch (result) {
        case -1:
          return generateMessage(responses.loose);
        case 1:
          return generateMessage(responses.win);
        default:
          return t('app.fight.drawFight');
      }
    }

    const ensureLastBattleFinished = async () => {
      const activeBattle = await getActiveBattle();
      await finishActiveBattleIfNotNull(activeBattle);
    }

    const fetchPendingMatches = async () => {
      const pendingMatchesResponse = await getPendingMatchesApi({
        asc: true,
        currency: 'TON',
        max_bet: '100',
        min_bet: '0',
        page: 0
      });
      setPendingMatches(pendingMatchesResponse.matches.content);
    }

    const acceptMatch = async (matchId: number) => {
      const response = await acceptMatchApi({matchId: matchId});
      setMatchModel(response.match);
      setBattleStage(BattleStage.STARTED);
    }

    const getPlayer = (isLeft: boolean) => {
      if (matchModel) {
        if (isLeft) {
          return user;
        } else {
          if (matchModel.creator.id !== user.id) {
            return matchModel.creator;
          }
          return matchModel.acceptor;
        }
      }
      return null;
    }


    const handleTurnFinishedEvent = (payload: TurnFinishedPayload) => {
      if (matchModel) {
        switch (matchModel.creator.id) {
          case leftPlayer?.id: {
            setHeroHpLeft(payload.result.creatorHitPoints);
            setEnemyHpLeft(payload.result.acceptorHitPoints);
            break;
          }
          case rightPlayer?.id: {
            setHeroHpLeft(payload.result.acceptorHitPoints);
            setEnemyHpLeft(payload.result.creatorHitPoints);
            break;
          }
          default: {
            break;
          }
        }
        setMatchModel({...matchModel, turn: matchModel.turn + 1});
      }
    }

    const handleMatchStartedEvent = (matchStartedPayload: MatchStartedPayload) => {
      setMatchModel(matchStartedPayload.matchModel);
      setBattleStage(BattleStage.STARTED);
    }

    useEffect(() => {
      console.log(matchEventsToHandle)
      if (!matchEventsToHandle || matchEventsToHandle.length <= 0) return;

      matchEventsToHandle.forEach(event => {
        if (matchModel && event.payload.matchId === matchModel.id) {
          switch (event.type) {
            case 'com.venividitap.server.domain.rps.events.MatchStartedEvent':
              handleMatchStartedEvent(event.payload as MatchStartedPayload);
              break;
            case 'com.venividitap.server.domain.rps.events.TurnFinishedEvent':
              handleTurnFinishedEvent(event.payload as TurnFinishedPayload);
              break;
            case 'com.venividitap.server.domain.rps.events.MatchFinishedEvent':
              handleFinishedMatch(event.payload as MatchFinishedPayload);
              break;
            default:
              break;
          }
        }
      });
    }, [matchEventsToHandle]);

    useEffect(() => {
      if (matchModel) {
        setLeftPlayer(getPlayer(true));
        setRightPlayer(getPlayer(false));
      }
    }, [matchModel]);

    useEffect(() => {
      if (checkGameIsOver()) {
        finishBattle().then(result => {
          handleFinishedBattle(result);
        }).catch(error => {
          setWarning(t(`app.errorCodes.${ error.code }`));
          setShowWarning(true);
        })
      }

      setGameOption(undefined);
    }, [battleModel]);

    useEffect(() => {
      if (battleStage === BattleStage.NOT_STARTED) {
        setIsFinishModalOpen(false);
        resetBattleModel();
        setShowWarning(false);
        setEnemyHpLeft(hp);
        setHeroHpLeft(hp);
        setIsFinishModalOpen(false);
        setBattleResultMessage("");
      }
      if (battleStage === BattleStage.STARTED) {
        setMessages([{text: t('app.fight.battleStarted'), incoming: true}]);
        setWarning(
          "app.fight.ifYouLeave"
        );
      }
      if (battleStage === BattleStage.FINISHED) {
        setWarning("");
        setIsFinishModalOpen(true);
      }
    }, [battleStage]);

    useEffect(() => {
      if (isFightModalOpen) setBattleStage(BattleStage.NOT_STARTED);
    }, [isFightModalOpen]);

    useEffect(() => {
      if (user) {
        const roleImageObj: IAppImage | undefined = roleImages.find(
          (image) => mapRace(image.value) === mapRace(user.race)
        );
        setMainCharacterIcon(roleImageObj ?? null);
      }
    }, [user, mainCharacterIcon]);

    useEffect(() => {
      const interval = setTimeout(() => {
        calculateBattleDuration();
      }, 1000)

      return () => clearInterval(interval);
    }, [battleTimeLeft]);

    useEffect(() => {
      const interval = setTimeout(() => {
        calculateCooldown();
      }, 1000)

      return () => clearInterval(interval);
    }, [fightCooldownRemaining]);

    useEffect(() => {
      calculateCooldown();
      calculateBattleDuration();
    }, [user, battleModel, matchModel]);

    useEffect(() => {
      if (isFightModalOpen) {
        fetchPendingMatches();
      }
    }, [isFightModalOpen]);

    useEffect(() => {
      ensureLastBattleFinished().catch(error => {
        setWarning(t(`app.errorCodes.${ error.code }`));
        setShowWarning(true);
      })
    }, []);

    if (battleStage === BattleStage.NOT_STARTED) {
      return (
        <>
          <FightStartScreen
            bet={ bet }
            user={ user }
            setBet={ setBet }
            fightCooldownRemaining={ fightCooldownRemaining }
            loading={ loading }
            onStartClick={ onStartClick }
            isPvp={ isPvp }
            setIsPvp={ setIsPvp }
            pendingMatches={ pendingMatches }
            acceptMatch={ acceptMatch }
          />
          { warning && showWarning && (
            <RPSWarningModal>
              <div className="flex flex-col h-full justify-between">
                <span className="text-xl m-auto">{ t(warning) }</span>
                <div>
                  <button
                    onClick={ () => {
                      setWarning("");
                      setShowWarning(false);
                    } }
                    className="w-full rounded-xl bg-red-700 p-2 mb-2"
                  >
                    { t("app.fight.close") }
                  </button>
                </div>
              </div>
            </RPSWarningModal>
          ) }
        </>
      );
    }

    return (
      <div className={ "p-2 grow relative flex flex-col justify-between overflow-auto" }>
        <div className="max-h-[calc(100%-100px)] grow bg-grayPrimary rounded-xl p-2 ">
          <div className={ "flex justify-between items-center" }>
            {
              isPvp ? (
                <RPSCharacter
                  image={ getFromStorage(`characters/${ leftPlayer?.gender }/${ leftPlayer?.race }/${ leftPlayer?.level }.webp`) }
                  alt={ leftPlayer?.race }
                  hp={ hp }
                  hpLeft={ heroHpLeft }
                />
              ) : (
                <RPSCharacter
                  image={ mainCharacterIcon?.getImage() }
                  alt={ mainCharacterIcon?.alt }
                  hp={ hp }
                  hpLeft={ heroHpLeft }
                />
              )
            }
            <div className="flex flex-col justify-center items-center text-center">
              <span>{ formatTimeMMSS(battleTimeLeft) }</span>
              <span className="text-4xl">VS</span>
              <span>{ t("app.fight.movesLeft") }:</span>
              { !isPvp && battleModel && (
                <span>
                  { battleModel.turn }/{ battleModel.maxTurns }
                </span>
              ) }
              { isPvp && matchModel && (
                <span>
                  { matchModel.turn }/{ MAX_ROUNDS }
                </span>
              ) }
            </div>
            {
              isPvp ? (
                <RPSCharacter
                  image={ getFromStorage(`characters/${ rightPlayer?.gender }/${ rightPlayer?.race }/${ rightPlayer?.level }.webp`) }
                  alt={ rightPlayer?.race }
                  hp={ 1000 }
                  hpLeft={ enemyHpLeft }
                />
              ) : (
                <RPSCharacter
                  alt={ "app.unknown" }
                  hp={ 100 }
                  hpLeft={ enemyHpLeft }
                  image={ getFromStorage(`battle/lvl${ user.level }/${ battleModel!.enemy + 1 }.webp`) }
                />
              )
            }
          </div>
          <div className="h-[calc(100%-150px)] overflow-auto my-3">
            <InternalChat messages={ messages }/>
          </div>
        </div>
        <RPSSelectOption
          battleStage={ battleStage }
          gameOption={ gameOption }
          handleGameOptionClick={ handleGameOptionClick }
          handleGameOptionSubmit={ handleGameOptionSubmit }
        />
        { battleStage === BattleStage.FINISHED && isFinishModalOpen && (
          <RPSWarningModal>
            <div className="flex flex-col h-full justify-between">
              <div className="text-xl text-center m-auto">
              <span className="text-3xl block mb-4">
                { t("app.fight.gameOver") }
              </span>
                {
                  battleResult && (
                    <>
                      { battleResultMessage }
                      <br/>
                      <span className={ 'mt-2 text-gold' }>
                        { '+' + battleResult.reward }
                      </span>
                    </>
                  )
                }
              </div>
              <button
                onClick={ closeResults }
                className="w-full rounded-xl bg-green-950 p-2 mt-2"
              >
                { t("app.fight.close") }
              </button>
            </div>
          </RPSWarningModal>
        ) }
        { warning && showWarning && (
          <RPSWarningModal>
            <div className="flex flex-col h-full justify-between">
              <span className="text-xl m-auto">{ t(warning) }</span>
              <div>
                <button
                  onClick={ () => closeFight() }
                  className="w-full rounded-xl bg-red-700 p-2 mb-2"
                >
                  { t("app.fight.closeAnyway") }
                </button>
                <button
                  onClick={ () => setShowWarning(false) }
                  className="w-full rounded-xl bg-green-950 p-2"
                >
                  { t("app.fight.continue") }
                </button>
              </div>
            </div>
          </RPSWarningModal>
        ) }
      </div>
    );
  }
;

export default Fight;
